import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';

import message from 'antd/es/message';
import Card from 'antd/es/card';
import Steps from 'antd/es/steps';
import Spin from 'antd/es/spin';

import WorkingHoursService from 'services/workingHours';

import MainLayout from 'layouts/Main.layout';
import { Row, Col, Space } from 'components/layout';
import PageHeader from 'components/MainPageHeader';
import Icon from 'components/Icon';
import Button from 'components/Button';

import ScheduleDetailsStep from '../steps/ScheduleDetails.step';
import BreakingTimesStep from '../steps/BreakingTimes.step';

import { dataToShifts, shiftsToData } from '../helpers';

const scheduleSteps = [
  {
    title: 'Schedule details',
    icon: <Icon name="calendar" size={24} />,
    content: ScheduleDetailsStep,
  },
  {
    title: 'Breaking times',
    icon: <Icon name="clock" size={24} />,
    content: BreakingTimesStep,
  },
];

const workingHoursService = new WorkingHoursService();

const SchedulePage = () => {
  const history = useHistory();
  const params = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [schedule, setSchedule] = useState({
    valid_from: null,
    valid_to: null,
  });
  const [shifts, setShifts] = useState([
    {
      shift_name: '1',
      workHours: [
        { start_hr: null, end_hr: null, workday: null }
      ],
      breakHours: [],
    }
  ]);
  const [beforeNext, setBeforeNext] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    if (params.id) {
      setIsLoading(true);
      Promise.all([
        workingHoursService.get({ schedule_id: params.id }),
        workingHoursService.listShifts({ schedule_id: params.id })
      ]).then(([scheduleData, shiftsData]) => {
        if (!scheduleData.length) return; 
        const { valid_from, valid_to } = scheduleData[0];
        setSchedule({
          ...scheduleData[0],
          valid_from: valid_from ? moment(valid_from) : null,
          valid_to: valid_to ? moment(valid_to) : null,
        });
        const shifts = dataToShifts(shiftsData);
        setShifts(shifts);
      })
      .finally(() => {
        setIsLoading(false);
      });
    }
  }, [params.id]);

  useEffect(() => {
    const mainRef = document.querySelector('main');
    if (mainRef) {
      mainRef.scrollTo({
        top: 0,
        left: 0,
      });
    }
  }, [currentStep]);

  const ActiveStep = useMemo(() => {
    return scheduleSteps[currentStep].content;
  }, [currentStep]);

  const handlePrevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({
        top: 0,
        left: 0,
      });
    }
  }, [currentStep]);
  
  const handleNextStep = useCallback(async () => {
    if (typeof beforeNext === 'function') {
      if (!(await beforeNext())) {
        return;
      }
    }

    if (currentStep < scheduleSteps.length - 1) {
      setCurrentStep(currentStep + 1);
      setBeforeNext(null);
    } else {
      setIsSubmitting(true);
      Promise.resolve()
        .then(() => {
          if (!params.id) {
            return workingHoursService.create({
              valid_from: schedule.valid_from,
              valid_to: schedule.valid_to,
            });
          } else {
            return workingHoursService.update({
              schedule_id: params.id,
              valid_from: schedule.valid_from,
              valid_to: schedule.valid_to,
            });
          }
        })
        .then(schedule => {
          const scheduleId = params.id || schedule.schedule_id;
          if (!scheduleId) {
            throw new Error('Could not get schedule id');
          }
          return workingHoursService.updateShifts({
            schedule_id: scheduleId,
            shifts: shiftsToData(shifts, scheduleId),
          });
        })
        .then(() => {
          history.replace(`/settings#workingHours`);
        })
        .catch(err => {
          console.log(err);
          message.error(`Schedule could not be ${params.id ? 'updated' : 'created'}`);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  }, [currentStep, beforeNext, schedule, shifts, history, params.id]);

  return (
    <MainLayout>
      <PageHeader
        title={`${params.id ? 'Edit' : 'Add'} Schedule`}
        icon="calendar"
      />
      <Row style={{ padding: '0 24px', height: 'calc(100vh - 92px)' }}>
        <Col span={24}>
          <StyledCard bordered={false}>
            <StyledSteps current={currentStep}>
              {
                scheduleSteps.map(step => (
                  <Steps.Step key={step.title} title={step.title} icon={step.icon} />
                ))
              }
            </StyledSteps>
            <Row style={{ padding: '16px 0' }}>
              <Col span={24}>
                {isLoading ? (
                  <Spin
                    size={30}
                    style={{ width: '100%', padding: '48px' }}
                  />
                ) : ActiveStep && (
                  <ActiveStep
                    schedule={schedule}
                    shifts={shifts}
                    setSchedule={setSchedule}
                    setShifts={setShifts}
                    setBeforeNext={fn => setBeforeNext(() => fn)}
                  />
                )}
              </Col>
            </Row>
            <Row justify="end" align="center">
              <Space>
                {currentStep > 0 ? (
                  <Button
                    type="default"
                    onClick={handlePrevStep}
                    style={{
                      width: '120px',
                      height: '38px'
                    }}
                  >
                    Back
                  </Button>
                ) : null}
                <Button
                  type="primary"
                  onClick={handleNextStep}
                  loading={isSubmitting}
                  style={{
                    width: '220px',
                    height: '38px'
                  }}
                >
                  {currentStep === scheduleSteps.length - 1
                    ? `${params.id ? 'Update' : 'Create'} Schedule`
                    : 'Next'
                  }
                </Button>
              </Space>
            </Row>
          </StyledCard>
        </Col>
      </Row>
    </MainLayout>
  );
};

const StyledCard = styled(Card)`
  background-color: #fff;
  height: 100%;
  padding: 24px 96px;

  & .ant-card-body {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const StyledSteps = styled(Steps)`
  width: 40vw;
  margin: 4px auto;
`;

export default SchedulePage;
